import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Modal, TouchableWithoutFeedback, View, useWindowDimensions, Platform, FlatList, ListRenderItemInfo } from 'react-native';
import styled, { DefaultTheme, useTheme } from 'styled-components/native';
import { AppPressable } from '@/components/base/app.pressable';
import { AppTextView } from '@/components/base/app.text.view';
import { AppLottieView } from '@/components/base/app.lottie.view';
import { Box, BoxProps } from '@/components/base/box';
import { useAssets } from '@/hooks/use.assets';

export interface DropdownItem {
  key: string;
  label: string;
}

interface Props {
  items: DropdownItem[];
  selectedKey?: string;
  onSelect: (key: string) => void;
  loading?: boolean;
  style?: BoxProps['style'];
  label?: string;
  labelTextColor?: keyof DefaultTheme['colors']['text'];
}

export function AppDropdownMenu({ items, selectedKey, onSelect, loading, style, label, labelTextColor = 'faded' }: Props) {
  const { width: screenWidth } = useWindowDimensions();
  const [visible, setVisible] = useState(false);
  const [menuTop, setMenuTop] = useState(0);
  const [menuCenterX, setMenuCenterX] = useState(0);
  const [menuWidth, setMenuWidth] = useState(0);
  const [widestOptionWidth, setWidestOptionWidth] = useState(0);

  const triggerRef = useRef<View>(null);
  const assets = useAssets();

  const selected = items.find((i) => i.key === selectedKey);

  // OPTIMIZATION 1: Find the longest label in plain JS to avoid rendering the whole list off-screen
  const longestItem = useMemo(() => {
    if (!items || items.length === 0) return null;
    return items.reduce((prev, current) => (prev.label.length > current.label.length ? prev : current));
  }, [items]);

  const openMenu = useCallback(() => {
    triggerRef.current?.measure((x, y, width, height, pageX, pageY) => {
      setMenuTop(pageY + height);
      setMenuCenterX(pageX + width / 2);
      setMenuWidth(width);
      setVisible(true);
    });
  }, []);

  const maxWidth = screenWidth * 0.9;
  const finalMenuWidth = Math.max(menuWidth, Math.min(widestOptionWidth, maxWidth));

  const SAFE_MARGIN = 16;
  let menuAbsoluteLeft = menuCenterX - finalMenuWidth / 2;

  if (menuAbsoluteLeft < SAFE_MARGIN) {
    menuAbsoluteLeft = SAFE_MARGIN;
  }

  if (menuAbsoluteLeft + finalMenuWidth > screenWidth - SAFE_MARGIN) {
    menuAbsoluteLeft = screenWidth - finalMenuWidth - SAFE_MARGIN;
  }

  return (
    <Box style={{ maxWidth: '95%', ...style }}>
      {/* OPTIMIZATION 2: Only render the SINGLE longest item to measure the max width */}
      <MeasureBox pointerEvents={'none'}>
        <MeasureContent onLayout={(e) => setWidestOptionWidth(Math.ceil(e.nativeEvent.layout.width))}>
          {longestItem && (
            <OptionItem active={false} style={Platform.OS === 'web' ? { width: 'max-content' } : undefined}>
              <OptionText active={false} text={longestItem.label} variant={'primary'} style={Platform.OS === 'web' ? { whiteSpace: 'nowrap' } : undefined} />
            </OptionItem>
          )}
        </MeasureContent>
      </MeasureBox>

      <RowContainer>
        <TriggerViewWrapper ref={triggerRef} collapsable={false}>
          <Trigger onPress={openMenu}>
            <TriggerContent>
              <TriggerText textColor={'blue'} variant={'primary'} numberOfLines={1}>
                {selected?.label}
              </TriggerText>
              <ChevronIcon $open={visible} />
            </TriggerContent>
          </Trigger>
        </TriggerViewWrapper>
        {label ? <LabelText text={label} textColor={labelTextColor} variant={'primary'} /> : null}
      </RowContainer>

      <Modal visible={visible} transparent onRequestClose={() => setVisible(false)} animationType="fade">
        <TouchableWithoutFeedback onPress={() => setVisible(false)}>
          <Overlay />
        </TouchableWithoutFeedback>

        <MenuContainer $top={menuTop} $left={menuAbsoluteLeft} $width={finalMenuWidth}>
          {loading ? (
            <LoadingContainer>
              <LoadingAnimation source={assets.animations.loading} loop />
            </LoadingContainer>
          ) : (
            /* OPTIMIZATION 3: Use FlatList so we only render what is actually visible on the screen */
            <OptionsList
              contentContainerStyle={{ flexGrow: 1 }}
              data={items}
              keyExtractor={(item: DropdownItem) => item.key}
              initialNumToRender={15}
              maxToRenderPerBatch={15}
              windowSize={5}
              showsVerticalScrollIndicator={true}
              renderItem={({ item }: ListRenderItemInfo<DropdownItem>) => {
                const isActive = selectedKey === item.key;
                return (
                  <OptionItem
                    active={isActive}
                    onPress={() => {
                      // OPTIMIZATION 4: Close the menu instantly, then tell the parent to update state in the next frame
                      setVisible(false);
                      setTimeout(() => onSelect(item.key), 0);
                    }}
                  >
                    <OptionText active={isActive} text={item.label} variant={'primary'} numberOfLines={1} />
                  </OptionItem>
                );
              }}
            />
          )}
        </MenuContainer>
      </Modal>
    </Box>
  );
}

const RowContainer = styled(Box)`
  flex-direction: row;
  align-items: center;
`;

const LabelText = styled(AppTextView)`
  margin-left: 8px;
  flex-shrink: 0;
`;

const Overlay = styled(Box)`
  flex: 1;
`;

const MenuContainer = styled(Box)<{ $top: number; $left: number; $width: number }>`
  position: absolute;
  top: ${({ $top }) => $top}px;
  left: ${({ $left }) => $left}px;
  width: ${({ $width }) => $width}px;
  background-color: white;
  border-radius: 12px;
  shadow-color: #000;
  shadow-offset: 0px 4px;
  shadow-opacity: 0.15;
  shadow-radius: 12px;
  elevation: 8;
  padding-vertical: 4px;
  overflow: hidden;
  z-index: 9999;
  ${Platform.OS === 'web' && `min-width: ${({ $width }: any) => $width}px;`}
`;

const LoadingContainer = styled(Box)`
  align-items: center;
  justify-content: center;
  padding-vertical: 16px;
`;

const OptionItem = styled(AppPressable)<{ active: boolean }>`
  flex-direction: row;
  align-items: center;
  padding-vertical: 12px;
  padding-horizontal: 10px;
  margin-horizontal: 5px;
  margin-vertical: 2px;
  border-radius: 8px;
  background-color: ${({ active, theme }) => (active ? theme.colors.list.background.primary : 'transparent')};
`;

const OptionText = styled(AppTextView)<{ active: boolean }>`
  color: ${({ active, theme }) => (active ? theme.colors.text.jade : theme.colors.text.black_light)};
  flex-shrink: 1;
`;

const Trigger = styled(AppPressable)`
  flex-direction: row;
  align-items: center;
  justify-content: center;
  background-color: white;
  border-width: 1.5px;
  border-color: ${({ theme }) => theme.colors.textField.border.primary};
  border-radius: 12px;
  padding-horizontal: 20px;
  padding-vertical: 12px;
  align-self: center;
  shadow-color: #000;
  shadow-offset: 0px 2px;
  shadow-opacity: 0.08;
  shadow-radius: 4px;
  elevation: 3;
  max-width: 100%;
`;

const TriggerContent = styled(Box)`
  flex-direction: row;
  align-items: center;
  gap: 10px;
  max-width: 100%;
`;

const TriggerText = styled(AppTextView)`
  margin-left: 6px;
  flex-shrink: 1;
`;

const MeasureBox = styled(Box)`
  position: absolute;
  opacity: 0;
  top: -10000px;
  left: 0px;
`;

const MeasureContent = styled(View)`
  align-items: flex-start;
`;

const TriggerViewWrapper = styled(View)`
  flex-shrink: 1;
`;

const LoadingAnimation = styled(AppLottieView)`
  width: ${({ theme }) => theme.hp(12)}px;
  height: ${({ theme }) => theme.hp(12)}px;
`;

const OptionsList = styled(FlatList)`
  max-height: ${({ theme }) => theme.hp(40)}px;
  direction: rtl;
`;

function ChevronIcon({ $open }: { $open: boolean }) {
  const theme = useTheme();
  const color = $open ? theme.colors.textField.border.blue : theme.colors.text.faded;
  return (
    <Box style={{ transform: [{ rotate: $open ? '180deg' : '0deg' }] }}>
      <Box
        style={{
          width: 0,
          height: 0,
          borderLeftWidth: 6,
          borderRightWidth: 6,
          borderTopWidth: 7,
          borderLeftColor: 'transparent',
          borderRightColor: 'transparent',
          borderTopColor: color,
        }}
      />
    </Box>
  );
}
